<template>
  <div class="date-range-picker-container">
    <div class="date-range-picker">
      <div>
        <label>{{ resource["dateSelectionTitle"] }}</label>
        <div class="calendar">
          <div class="month-navigation">
            <button @click="prevMonth" :disabled="isPrevMonthDisabled">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M14.9995 6.70461C14.6095 6.31461 13.9795 6.31461 13.5895 6.70461L8.99953 11.2946C8.60953 11.6846 8.60953 12.3146 8.99953 12.7046L13.5895 17.2946C13.9795 17.6846 14.6095 17.6846 14.9995 17.2946C15.3895 16.9046 15.3895 16.2746 14.9995 15.8846L11.1195 11.9946L14.9995 8.11461C15.3895 7.72461 15.3795 7.08461 14.9995 6.70461Z"
                  fill="#333333"
                />
              </svg>
            </button>
            <span>{{ currentMonthName }} {{ currentYear }}</span>
            <button @click="nextMonth" :disabled="isNextMonthDisabled">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M8.99953 6.71075C8.60953 7.10075 8.60953 7.73075 8.99953 8.12075L12.8795 12.0008L8.99953 15.8808C8.60953 16.2708 8.60953 16.9008 8.99953 17.2908C9.38953 17.6808 10.0195 17.6808 10.4095 17.2908L14.9995 12.7008C15.3895 12.3108 15.3895 11.6808 14.9995 11.2908L10.4095 6.70075C10.0295 6.32075 9.38953 6.32075 8.99953 6.71075Z"
                  fill="#333333"
                />
              </svg>
            </button>
          </div>
          <div class="weekdays">
            <!-- v-b-tooltip="{customClass: 'feature-tooltip-date-range', title: day.long}" -->
            <span
              v-for="(day, index) in weekdays"
              :key="day.narrow + currentMonthName + index"
              :id="day.narrow + currentMonthName + index"
              >{{ day.narrow }}</span
            >
          </div>
          <div class="days">
            <div
              v-for="(day, index) in days"
              :key="day.date + currentMonthName + index"
              class="day-container"
            >
              <span :class="getDayClass(day)" @click="selectDate(day.date)">
                {{ day.day }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="time-selection">
        <div class="time-input">
          <label for="start-time"
            >{{ resource["scheduleStartTime"]
            }}<i
              id="feature-tooltip-start-time"
              class="icon-info cursor-pointer"
            ></i
          ></label>
          <b-tooltip
            target="feature-tooltip-start-time"
            placement="bottom"
            :delay="{ show: 250, hide: 200 }"
            custom-class="feature-tooltip-date-range"
            boundary="viewport"
          >
            {{ resource["scheduleStartTimeTooltip"] }}
          </b-tooltip>
          <multiselect
            ref="multiselectStartTime"
            v-model="startTime"
            placeholder="00:00"
            :options="filteredTimeList"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            :hide-selected="false"
            :max-height="210"
            :disabled="isStartDateFixed || disabledStartTime()"
            :taggable="true"
            tagPlaceholder="Add new time"
            @tag="addTag($event, 'startTime')"
            @close="handleClose"
            :searchQuery="startSearchQuery"
            @input="(val) => handleInput(val, 'startTime')"
            @search-change="(query) => handleTimeSearch(query, 'startTime')"
          >
            <template #singleLabel="props">
              <span class="option__desc">
                <span class="option__title">
                  {{ props.option.label ? props.option.label : props.option }}
                </span>
              </span>
            </template>
            <template #option="props">
              <div class="option__desc">
                <span class="option__title">{{
                  props.option.label ? props.option.label : props.option
                }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <div class="time-input duration">
          <label>
            {{ resource["scheduleDuration"] }}
            <i
              id="feature-tooltip-duration"
              class="icon-info cursor-pointer"
            ></i>
          </label>
          <b-tooltip
            target="feature-tooltip-duration"
            placement="bottom"
            :delay="{ show: 250, hide: 200 }"
            custom-class="feature-tooltip-date-range"
            boundary="viewport"
          >
            {{ resource["scheduleDurationTooltip"] }}
          </b-tooltip>
          <multiselect
            ref="multiselectDuration"
            v-model="selectedDuration"
            placeholder="Select duration"
            :options="durationOptions"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            :hide-selected="false"
            :max-height="210"
          >
            <template #singleLabel="props">
              <span class="option__desc">
                <span class="option__title">
                  {{ props.option.label ? props.option.label : props.option }}
                </span>
              </span>
            </template>
            <template #option="props">
              <div class="option__desc">
                <span class="option__title">{{
                  props.option.label ? props.option.label : props.option
                }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <div class="time-input">
          <label for="end-time"
            >{{ resource["scheduleEndTime"]
            }}<i
              id="feature-tooltip-end-time"
              class="icon-info cursor-pointer"
            ></i
          ></label>
          <b-tooltip
            target="feature-tooltip-end-time"
            placement="bottom"
            :delay="{ show: 250, hide: 200 }"
            custom-class="feature-tooltip-date-range"
            boundary="viewport"
          >
            {{ resource["scheduleEndTimeTooltip"] }}
          </b-tooltip>
          <multiselect
            ref="multiselectEndTime"
            v-model="endTime"
            placeholder="00:00"
            :options="filteredTimeList"
            :multiple="false"
            :searchable="true"
            :close-on-select="true"
            :hide-selected="false"
            :max-height="210"
            :taggable="true"
            tagPlaceholder="Add new time"
            @tag="addTag($event, 'endTime')"
            @close="handleClose"
            :searchQuery="endSearchQuery"
            @input="(val) => handleInput(val, 'endTime')"
            @search-change="(query) => handleTimeSearch(query, 'endTime')"
          >
            <template #singleLabel="props">
              <span class="option__desc">
                <span class="option__title">
                  {{ props.option.label ? props.option.label : props.option }}
                </span>
              </span>
            </template>
            <template #option="props">
              <div class="option__desc">
                <span class="option__title">{{
                  props.option.label ? props.option.label : props.option
                }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <span class="time-note">
          {{ resource["scheduleTimeNote"] }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import ResourceManager from '@/services/resource.service';

export default {
  name: 'DatePicker',
  components: {
    Multiselect,
  },
  props: {
    value: {
      type: Object,
      default: () => ({ startDate: null, endDate: null }),
    },
    isStartDateFixed: {
      type: Boolean,
      default: false,
    },
    isDateSet: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth(),
      today: new Date(),
      internalStartDate: this.value.startDate,
      internalEndDate: this.value.endDate,
      selectingEndDate: false,
      weekdays: [],
      minDate: new Date(),
      maxDate: (() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return date;
      })(),
      startTime: null,
      endTime: null,
      timeList: [],
      originalTimeList: [],
      searchQuery: '',
      startSearchQuery: '',
      endSearchQuery: '',
      selectedDuration: '7', // Changed from this.value.selectedDuration || null to always default to '7'
      durationOptions: [],
    };
  },
  created() {
    this.generateTimeList();
    const now = new Date();

    this.startTime = this.formatTime(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
      ),
    );

    this.endTime = this.formatTime(
      new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
      ),
    );

    if (this.isStartDateFixed) {
      this.internalStartDate = now;
    } else {
      if (this.value.selectedDuration) {
        this.selectedDuration = this.value.selectedDuration;
      }
      if (this.value.startDate) {
        // if (this.isDateSet) {
        //   this.minDate = new Date(this.value.startDate);
        // }
        this.internalStartDate = new Date(this.value.startDate);
      }
      if (this.value.startTime) {
        this.startTime = this.value.startTime;
      }
    }

    if (this.value.endDate) {
      this.internalEndDate = new Date(this.value.endDate);
    }
    if (this.value.endTime) {
      this.endTime = this.value.endTime;
    }

    this.errorTypes = {
      REQUIRED: this.resource?.scheduleRequiredError,
      MIN_DAY: this.resource?.scheduleMinDayError,
      MAX_DAY: this.resource?.scheduleMaxDayError,
      AUTO_ADJUSTED_TIME: this.resource?.scheduleMinDayError,
      INVALID_TIME_RANGE: this.resource?.scheduleInvalidTimeRangeError,
    };

    this.durationOptions = [
      this.resource?.scheduleSameDay,
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
    ];

    const language = typeof ResourceManager.CurrentLang !== 'string' ? ResourceManager.CurrentLang.key : ResourceManager.CurrentLang;
    const date = new Date();
    this.weekdays = [...Array(7).keys()].map((_, i) => {
      const day = new Date(date.setDate(date.getDate() - date.getDay() + i));
      return {
        narrow: day.toLocaleString(language, { weekday: 'narrow' }).toLowerCase(),
        long: day.toLocaleString(language, { weekday: 'long' }),
      };
    });

    this.$nextTick(() => {
      this.validateStartTime();
    });
  },
  computed: {
    filteredTimeList() {
      if (!this.searchQuery) {
        return this.timeList;
      }
      return this.timeList.filter((time) => time.toLowerCase().includes(this.searchQuery.toLowerCase()));
    },
    currentMonthName() {
      const language = typeof ResourceManager.CurrentLang !== 'string' ? ResourceManager.CurrentLang.key : ResourceManager.CurrentLang;
      return new Date(this.currentYear, this.currentMonth).toLocaleString(
        language,
        {
          month: 'long',
        },
      );
    },
    days() {
      const days = [];
      const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
      const lastDayOfMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0,
      );
      const startDay = firstDayOfMonth.getDay();
      const totalDays = lastDayOfMonth.getDate();

      // eslint-disable-next-line no-plusplus
      for (let i = startDay - 1; i >= 0; i--) {
        const date = new Date(this.currentYear, this.currentMonth, -i);
        days.push({ day: date.getDate(), date, isOtherMonth: true });
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= totalDays; i++) {
        const date = new Date(this.currentYear, this.currentMonth, i);
        days.push({ day: i, date, isOtherMonth: false });
      }

      const remainingDays = 42 - days.length;
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(this.currentYear, this.currentMonth + 1, i);
        days.push({ day: date.getDate(), date, isOtherMonth: true });
      }

      return days;
    },
    isPrevMonthDisabled() {
      return new Date(this.currentYear, this.currentMonth, 1) <= this.minDate;
    },
    isNextMonthDisabled() {
      return (
        new Date(this.currentYear, this.currentMonth + 1, 0) >= this.maxDate
      );
    },
  },
  watch: {
    startTime: {
      handler(newVal) {
        this.updateStartDateTime();

        // When duration is "Same Day", automatically set end time 30 minutes later
        if (this.selectedDuration === this.resource?.scheduleSameDay && newVal) {
          const [hours, minutes] = newVal.split(':').map(Number);
          const startDateTime = new Date();
          startDateTime.setHours(hours, minutes, 0, 0);

          const endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 30);

          this.endTime = this.formatTime(endDateTime);
        }
      },
      immediate: false,
    },
    endTime(newVal) {
      if (this.internalStartDate && this.internalEndDate) {
        const [startHours, startMinutes] = this.startTime
          .split(':')
          .map(Number);
        const [endHours, endMinutes] = newVal.split(':').map(Number);

        const startDateTime = new Date(this.internalStartDate);
        const endDateTime = new Date(this.internalEndDate);

        startDateTime.setHours(startHours, startMinutes, 0, 0);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
      }
      this.updateEndDateTime();
    },
    internalStartDate() {
      this.validateStartTime();
      this.emitSelectedDates();
    },
    internalEndDate() {
      this.emitSelectedDates();
    },
    selectedDuration: {
      handler() {
        if (this.internalStartDate) {
          this.calculateEndDate();
        }
      },
    },
  },
  methods: {
    disabledStartTime() {
      // Eğer edit ise ve başlangıç tarihi seçilmişse ve bugünden önceyse, disable et

      // if (this.isDateSet && this.startTime < this.formatTime(new Date())) {
      //   return true;
      // }
    },
    validateStartTime() {
      // if (!this.internalStartDate || !this.startTime || this.isDateSet) return;
      if (!this.internalStartDate || !this.startTime) return;

      const now = new Date();
      if (this.isSameDay(this.internalStartDate, now)) {
        const [hours, minutes] = this.startTime.split(':').map(Number);
        const selectedTime = new Date(this.internalStartDate);
        selectedTime.setHours(hours, minutes, 0, 0);

        if (selectedTime < now) {
          this.startTime = this.formatTime(now);
          this.internalStartDate.setHours(
            now.getHours(),
            now.getMinutes(),
            0,
            0,
          );
          this.emitSelectedDates();
        }
      }
    },
    addTag(newTag, name) {
      const tagValue = typeof newTag === 'object' ? newTag.label : newTag;
      const formattedTime = this.formatTimeInput(tagValue);

      if (this.isValidTimeFormat(formattedTime)) {
        const [rawHours, rawMinutes = ''] = formattedTime.split(':');

        // Process hours
        let hours = rawHours.slice(0, 2).padStart(2, '0');
        hours = Math.min(23, Math.max(0, parseInt(hours, 10) || 0))
          .toString()
          .padStart(2, '0');

        // Process minutes
        let minutes = rawMinutes.slice(0, 2).padStart(2, '0');
        minutes = Math.min(59, Math.max(0, parseInt(minutes, 10) || 0))
          .toString()
          .padStart(2, '0');

        const finalTime = `${hours}:${minutes}`;

        if (!this.originalTimeList.includes(finalTime)) {
          this.timeList.unshift(finalTime);
          this.originalTimeList.unshift(finalTime);
        }

        const now = new Date();
        const [inputHours, inputMinutes] = formattedTime.split(':').map(Number);
        const inputTime = new Date();
        inputTime.setHours(inputHours, inputMinutes, 0, 0);

        if (name === 'startTime') {
          if (this.isSameDay(this.internalStartDate, now) && inputTime < now) {
            this[name] = this.formatTime(now);
          } else {
            this[name] = formattedTime;
          }

          // EndDate seçili ve bugün ise endTime'ı güncelle
          if (
            this.internalEndDate
            && this.isSameDay(this.internalEndDate, now)
          ) {
            const minEndTime = new Date(inputTime);
            minEndTime.setMinutes(minEndTime.getMinutes() + 30);

            if (this.endTime) {
              const [endHours, endMinutes] = this.endTime
                .split(':')
                .map(Number);
              const currentEndTime = new Date();
              currentEndTime.setHours(endHours, endMinutes, 0, 0);

              if (currentEndTime < minEndTime) {
                this.endTime = this.formatTime(minEndTime);
              }
            }
          }
        } else if (name === 'endTime') {
          const startDateTime = new Date(this.internalStartDate);
          const [startHours, startMinutes] = this.startTime
            .split(':')
            .map(Number);
          startDateTime.setHours(startHours, startMinutes, 0, 0);

          const endDateTime = new Date(this.internalEndDate);
          endDateTime.setHours(inputHours, inputMinutes, 0, 0);

          // Minimum 30 dakika kontrolü
          const minEndTime = new Date(startDateTime);
          minEndTime.setMinutes(minEndTime.getMinutes() + 30);

          // Maksimum 8 gün kontrolü
          const maxEndTime = new Date(startDateTime);
          maxEndTime.setDate(maxEndTime.getDate() + 8);

          if (endDateTime < minEndTime) {
            this[name] = this.formatTime(minEndTime);
          } else if (endDateTime > maxEndTime) {
            this[name] = this.formatTime(maxEndTime);
          } else {
            this[name] = formattedTime;
          }
        }

        this.updateStartDateTime();
        this.updateEndDateTime();
      }

      this.searchQuery = '';
      this.handleClose();

      // Input'u temizle
      if (this.$refs.multiselect) {
        this.$refs.multiselect.search = '';
      }
    },
    formatTimeInput(input) {
      // Eğer input boşsa veya undefined ise
      if (!input) {
        return '';
      }

      // 1. Tüm non-digit karakterleri temizle
      let nums = input.replace(/\D/g, '');

      // 2. İlk 4 karakteri al (HHmm)
      nums = nums.slice(0, 4);

      // 3. Sayıları parse et ve formatlama kurallarını uygula
      let hours = '';
      let mins = '';

      if (nums.length <= 2) {
        // Sadece saat girişi varsa
        hours = nums;
        if (Number(hours) > 23) {
          hours = '23';
        }
      } else {
        // Saat ve dakika girişi varsa
        hours = nums.slice(0, 2);
        mins = nums.slice(2);

        if (Number(hours) > 23) {
          hours = '23';
        }

        if (Number(mins) > 59) {
          if (mins.length === 1) {
            mins = `0${mins[0]}`;
          } else {
            mins = '59';
          }
        }
      }

      // 4. Formatı oluştur
      let formatted = hours;
      if (nums.length > 2) {
        formatted += `:${mins}`;
      } else if (nums.length === 2) {
        formatted += ':';
      }

      return formatted;
    },
    isValidTimeFormat(time) {
      // Check if the time matches HH:mm format and is valid
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      return timeRegex.test(time);
    },
    handleTimeSearch(query, name) {
      const formatted = this.formatTimeInput(query);
      if (name === 'startTime') {
        this.startSearchQuery = formatted;
        this.$refs.multiselectStartTime.search = formatted;
      } else {
        this.endSearchQuery = formatted;
        this.$refs.multiselectEndTime.search = formatted;
      }

      this.timeList = this.originalTimeList.filter((t) => t.includes(formatted));
    },
    handleClose() {
      this.$nextTick(() => {
        this.timeList = [...this.originalTimeList];
        this.searchQuery = '';
      });
    },

    handleInput(val, name) {
      if (!val) {
        this.timeList = [...this.originalTimeList];
        return;
      }
      const formatted = this.formatTimeInput(val);
      if (name === 'startTime') {
        this.startSearchQuery = formatted;
        this.startTime = formatted;
      } else {
        this.endSearchQuery = formatted;
        this.endTime = formatted;
      }
    },
    formatTime(date) {
      return date
        .toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // 24 saatlik format
        })
        .replace(/^24:/, '00:'); // Gece yarısı için düzeltme
    },
    generateTimeList() {
      const times = [];
      // eslint-disable-next-line no-plusplus
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMinute = minute.toString().padStart(2, '0');
          times.push(`${formattedHour}:${formattedMinute}`);
        }
      }
      this.timeList = times;
      this.originalTimeList = times;
    },
    updateStartDateTime() {
      if (this.internalStartDate && this.startTime) {
        const [hours, minutes] = this.startTime.split(':').map(Number);
        this.internalStartDate.setHours(hours, minutes, 0, 0);
        this.validateStartTime();

        // When duration is "Same Day", automatically set end time 30 minutes later
        if (this.selectedDuration === this.resource?.scheduleSameDay) {
          const startDateTime = new Date(this.internalStartDate);
          startDateTime.setHours(hours, minutes, 0, 0);

          const endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 30);

          // Set end date to same as start date
          this.internalEndDate = new Date(this.internalStartDate);
          this.endTime = this.formatTime(endDateTime);
        }

        this.emitSelectedDates();
      }
    },
    updateEndDateTime() {
      if (this.internalEndDate && this.endTime) {
        const [hours, minutes] = this.endTime.split(':').map(Number);
        const startDateTime = new Date(this.internalStartDate);
        const endDateTime = new Date(this.internalEndDate);

        endDateTime.setHours(hours, minutes, 0, 0);

        // Minimum 30 dakika ve maksimum 8 gün kontrolü
        const diffInHours = (endDateTime - startDateTime) / (1000 * 60 * 60);
        const diffInDays = diffInHours / 24;

        if (diffInHours < 0.5) {
          endDateTime.setDate(startDateTime.getDate());
          endDateTime.setHours(
            startDateTime.getHours(),
            startDateTime.getMinutes() + 30,
            0,
            0,
          );
          this.endTime = this.formatTime(endDateTime);
        } else if (diffInDays > 8) {
          // 8 günden fazla olursa, 8. güne ayarla
          const newEndDate = this.addDays(startDateTime, 8);
          newEndDate.setHours(
            startDateTime.getHours(),
            startDateTime.getMinutes(),
            0,
            0,
          );
          this.internalEndDate = newEndDate;
          this.endTime = this.startTime;
        }

        this.internalEndDate = endDateTime;
        this.emitSelectedDates();
      }
    },
    emitSelectedDates() {
      const formattedStartDate = this.internalStartDate
        ? this.internalStartDate.toISOString()
        : null;
      const formattedEndDate = this.internalEndDate
        ? this.internalEndDate.toISOString()
        : null;

      this.$emit('input', {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        startTime: this.startTime,
        endTime: this.endTime,
        selectedDuration: this.selectedDuration,
      });
    },
    prevMonth() {
      if (!this.isPrevMonthDisabled) {
        if (this.currentMonth === 0) {
          this.currentMonth = 11;
          // eslint-disable-next-line no-plusplus
          this.currentYear--;
        } else {
          // eslint-disable-next-line no-plusplus
          this.currentMonth--;
        }
      }
    },
    nextMonth() {
      if (!this.isNextMonthDisabled) {
        if (this.currentMonth === 11) {
          this.currentMonth = 0;
          // eslint-disable-next-line no-plusplus
          this.currentYear++;
        } else {
          // eslint-disable-next-line no-plusplus
          this.currentMonth++;
        }
      }
    },
    selectDate(date) {
      if (!date) return;

      // Tarihleri normalize et
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);

      const minNormalizedDate = new Date(this.minDate);
      minNormalizedDate.setHours(0, 0, 0, 0);

      const maxNormalizedDate = new Date(this.maxDate);
      maxNormalizedDate.setHours(0, 0, 0, 0);

      // Tarih validasyonları
      if (
        normalizedDate > maxNormalizedDate
        || normalizedDate < minNormalizedDate
      ) return;

      this.internalStartDate = new Date(normalizedDate);
      this.calculateEndDate();
      this.updateStartDateTime();
    },
    calculateEndDate() {
      if (!this.internalStartDate) return;

      const startDateTime = new Date(this.internalStartDate);
      let daysToAdd = 0;

      // eslint-disable-next-line default-case
      switch (this.selectedDuration) {
        case this.resource?.scheduleSameDay:
          daysToAdd = 0;
          // For same day, set end time to start time + 30 minutes
          // eslint-disable-next-line no-case-declarations
          const [hours, minutes] = this.startTime.split(':').map(Number);
          // eslint-disable-next-line no-case-declarations
          const endDateTime = new Date(startDateTime);
          endDateTime.setHours(hours, minutes + 30, 0, 0);
          this.internalEndDate = new Date(startDateTime);
          this.endTime = this.formatTime(endDateTime);
          break;
        case '1':
          daysToAdd = 1;
          break;
        case '2':
          daysToAdd = 2;
          break;
        case '3':
          daysToAdd = 3;
          break;
        case '4':
          daysToAdd = 4;
          break;
        case '5':
          daysToAdd = 5;
          break;
        case '6':
          daysToAdd = 6;
          break;
        case '7':
          daysToAdd = 7;
          break;
      }

      // When not same day, use the original logic
      if (this.selectedDuration !== this.resource?.scheduleSameDay) {
        const endDateTime = this.addDays(startDateTime, daysToAdd);

        if (this.endTime) {
          const [hours, minutes] = this.endTime.split(':').map(Number);
          endDateTime.setHours(hours, minutes, 0, 0);
        } else {
          const [hours, minutes] = this.startTime.split(':').map(Number);
          endDateTime.setHours(hours, minutes, 0, 0);
        }

        this.internalEndDate = endDateTime;
      }

      this.updateEndDateTime();
      this.emitSelectedDates();
    },
    getDayClass(day) {
      if (!day.date) return 'empty';

      const classList = [];
      const isToday = day.date.getFullYear() === this.today.getFullYear()
        && day.date.getMonth() === this.today.getMonth()
        && day.date.getDate() === this.today.getDate();

      if (day.isOtherMonth) classList.push('other-month');

      // Başlangıç tarihi seçilmişse, 8 günden sonrasını disable et
      if (this.internalStartDate && !this.internalEndDate) {
        const maxDate = new Date(this.internalStartDate);
        maxDate.setDate(maxDate.getDate() + 8);
        if (day.date > maxDate) {
          classList.push('disabled');
        }
      }

      // Normal disable kontrolü
      if (!isToday && (day.date < this.minDate || day.date > this.maxDate)) {
        classList.push('disabled');
      }

      if (isToday) {
        classList.push('today');
      }

      // Start date seçiliyse ve bugünden önceki tarihleri disable et
      if (
        this.isStartDateFixed
        && day.date < this.internalStartDate
        && !isToday
      ) {
        classList.push('no-pointer-events');
      }

      if (
        this.internalStartDate
        && day.date.getFullYear() === this.internalStartDate.getFullYear()
        && day.date.getMonth() === this.internalStartDate.getMonth()
        && day.date.getDate() === this.internalStartDate.getDate()
      ) {
        classList.push('start-date');
      }

      return classList.length ? `day ${classList.join(' ')}` : 'day';
    },
    addDays(date, days) {
      const result = new Date(date);

      // Gün gün ekleyerek ay sınırlarını kontrol et
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < days; i++) {
        result.setDate(result.getDate() + 1);

        // Eğer ay değiştiyse ve gün 1 değilse, ayın son gününe ayarla
        if (result.getDate() === 1 && i < days - 1) {
          result.setDate(0); // Bir önceki ayın son günü
          result.setDate(result.getDate() + 1);
        }
      }

      return result;
    },
    isSameDay(date1, date2) {
      if (!date1 || !date2) return false;
      return (
        date1.getFullYear() === date2.getFullYear()
        && date1.getMonth() === date2.getMonth()
        && date1.getDate() === date2.getDate()
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/style/starter.scss";

.date-range-picker-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.date-range-picker {
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 16px;

  @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
    flex-direction: column;
  }

  label {
    display: flex;
    align-items: center;
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Subtitle/3 - Medium */
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 4px !important;
  }

  i {
    font-size: 16px;
    margin-left: 4px;
  }
}

.calendar {
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  background: #fff;
  padding: 16px;
}

.month-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 4px;
  width: fit-content;

  span {
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Subtitle/3 - Medium */
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }

  button {
    padding: 0px !important;
    height: auto !important;
    border-radius: 0 !important;
    outline: 0 !important;
    border: none;

    &:disabled {
      background: transparent !important;
      opacity: 0.3 !important;
      cursor: not-allowed;
    }
  }
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 30px);
  text-align: center;
  font-weight: bold;
  margin-bottom: 5px;

  @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
    grid-template-columns: repeat(7, 1fr);
  }

  span {
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    font-family: Roboto;
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    opacity: 0.7;
  }
}

.days {
  display: grid;
  grid-template-columns: repeat(7, 30px);
  text-align: center;

  @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
    grid-template-columns: repeat(7, 1fr);
  }
}

.day-container {
  display: flex;
  padding: 4px;
  align-items: flex-start;
  gap: 10px;
  height: 30px;

  @media only screen and (max-width: map-get($grid-breakpoints, "md")) {
    justify-content: center;
  }

  &:has(.start-date) {
    padding-right: 0;
  }
  &:has(.start-date):has(.today):has(.no-pointer-events) {
    padding-right: 4px;
  }
  &:has(.end-date) {
    padding-left: 0;
  }

  &:has(.today):has(.start-date):has(.no-pointer-events) {
    .today {
      border-radius: 100%;
    }
    padding-left: 4px !important;
  }
}

.days {
  &:has(.today):has(.start-date):has(.no-pointer-events):has(.in-range) {
    .day-container {
      padding-right: 0 !important;
    }
  }
}

.day,
.other-month,
.empty {
  cursor: pointer;
  color: var(--Text-Colors-Primary-Gray-Text, #333);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  display: flex;
  justify-content: center;

  width: 22px;
  padding: 5px;
  align-items: flex-start;
  gap: 10px;
}

.other-month {
  color: #878787 !important;
  opacity: 0.7;
}

.today {
  border-radius: 99px;
  border: 1px solid #ffe9ce;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 22px;
}

.disabled {
  color: #878787;
  cursor: not-allowed;
  pointer-events: all;
  opacity: 0.3;
}

.start-date {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.start-date.end-date {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.start-end-date {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.single-day {
  background-color: #ffe9ce;
  border-radius: 99px;
}

.in-range {
  background-color: #ffe9ce;
}

.selected-range {
  margin-top: 15px;
  font-weight: bold;
}

.time-selection {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.time-input {
  display: flex;
  flex-direction: column;

  label {
    display: flex;
    align-items: center;
    color: var(--Text-Colors-Primary-Gray-Text, #333);
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Subtitle/3 - Medium */
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 4px !important;
  }

  i {
    font-size: 16px;
    margin-left: 4px;
  }
}

.time-note {
  color: var(--Text-Colors-Secondary-Gray-Text, #878787);
  font-feature-settings: "liga" off, "clig" off;
  /* Typography/Small Text/1 - Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  max-width: 300px;
  margin-top: 8px;
}

.info-box {
  width: 500px;
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  margin: 24px auto 0;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--Stroke-Colors-Primary-Gray-Stroke, #e5e5e5);
  background: var(--Background-Colors-Bright-Gray, #fafafa);
  color: var(--Text-Colors-Primary-Gray-Text, #333);
  font-feature-settings: "liga" off, "clig" off;
  /* Typography/Small Text/1 - Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 150% */

  i {
    font-size: 20px;
  }

  &--error {
    border: 1px solid var(--Stroke-Colors-Light-Red-Stroke, #f2b5b5);
    background: var(--Background-Colors-Light-Red, #f8dada);
    color: #631212;
  }
}

.quick-select-buttons {
  display: flex;
  gap: 10px;
  margin-top: 10px;

  button {
    padding: 5px 10px;
    background-color: #ffe9ce;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: #ffd580;
    }
  }
}

.multiselect {
  max-width: 300px;
  height: 36px;
  min-height: 36px;
}

::v-deep {
  .multiselect__select {
    height: 40px !important;
    min-height: 38px !important;
    padding: 8px !important;
    top: 0;
    right: 0;
    border-radius: 4px;
    border: 1px solid var(--Stroke-Colors-Tertiary-Gray-Stroke, #c3c3c3);
  }

  .multiselect__tags {
    height: 40px !important;
    min-height: 38px !important;
    padding: 8px !important;
  }

  .multiselect__single {
    margin-bottom: 0;
  }

  .multiselect__placeholder,
  .multiselect__single {
    margin-bottom: 0;
    display: flex;
    width: 100%;
    align-items: center;
    height: 100%;
  }

  .multiselect__option--selected {
    background: #eee;
  }

  .option__title {
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #333;
  }

  .multiselect__content-wrapper {
    z-index: 999;
    top: 48px;
    padding: 4px 0;
    border-radius: 4px !important;
    border-color: #e5e5e5;
    box-shadow: 0px 2px 12px 1px rgba(0, 0, 0, 0.12);

    &::-webkit-scrollbar {
      width: 7px !important;
    }

    &::-webkit-scrollbar-track {
      background-color: #fff !important;
      border-radius: 2px !important;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 2px !important;
      background: linear-gradient(to right, #D8D8D8 3px, #FFF 4px) !important;
    }
  }

  .multiselect__content {
    box-shadow: none;
  }

  .multiselect__option {
    padding: 6px 16px;
    height: 36px;
    display: flex;
    align-items: center;
  }

  .multiselect__option--highlight {
    background: #EEEEEE;
  }

  .multiselect__option--selected:after {
    display: none;
  }

  .duration {
    .multiselect__select {
      &::before {
        display: block !important;
      }
    }
  }

  .multiselect__select:before {
    display: none;
    content: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none"><path d="M7.25537 10.0109L9.4137 12.1693C9.7387 12.4943 10.2637 12.4943 10.5887 12.1693L12.747 10.0109C13.272 9.48594 12.897 8.58594 12.1554 8.58594H7.8387C7.09703 8.58594 6.73037 9.48594 7.25537 10.0109Z" fill="%23333333"/></svg>');
    border: none !important;
    top: 1px !important;
    margin: 0 !important;
  }

  .multiselect__input {
    margin-bottom: 0px !important;
  }

  .multiselect--active {
    .multiselect__select {
      box-shadow: 0px 0px 0px 4px rgba(255, 141, 6, 0.3) !important;
      outline: 1px solid #ff8d06 !important;
    }
  }
}

.feature-tooltip-date-range {
  opacity: 1 !important;

  &.bs-tooltip-right {
    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>');
        width: 17px;
        height: 9px;
      }
    }
  }

  &.bs-tooltip-left {
    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>');
        width: 17px;
        height: 9px;
        transform: rotate(180deg);
      }
    }
  }

  &.bs-tooltip-bottom {
    padding: 0 !important;
    top: 10px !important;

    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>') !important;
        width: 17px;
        height: 9px;
        bottom: 5px;
      }
    }
  }

  &.bs-tooltip-top {
    padding: 0 !important;
    top: 10px !important;

    ::v-deep .arrow {
      &::before {
        border: 0 !important;
        background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="17" height="9" viewBox="0 0 17 9" fill="none"><path d="M17 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9Z" fill="%23DBF0F9"/><path fill-rule="evenodd" clip-rule="evenodd" d="M1.5 9L0 9L7.04598 1.53956C7.83522 0.703886 9.16478 0.703887 9.95403 1.53956L17 9L15.5 9L14.6801 8L9.22701 2.22618C8.83239 1.80835 8.16761 1.80835 7.77299 2.22618L2.31993 8L1.5 9Z" fill="%23B7E1F3"/></svg>') !important;
        width: 17px;
        height: 9px;
        top: 5px;
        transform: rotate(180deg);
      }
    }
  }

  ::v-deep .tooltip-inner {
    border: 1px solid var(--Stroke-Colors-Light-Blue-Stroke, #b7e1f3) !important;
    background: var(--Background-Colors-Light-Blue, #dbf0f9) !important;
    border-radius: 4px !important;
    padding: 8px 12px !important;
    width: fit-content !important;
    max-width: fit-content !important;
    box-shadow: 0px 2px 6px 0px rgba(176, 186, 197, 0.6) !important;

    text-align: left;
    color: var(--Text-Colors-Blue-Text, #114e67) !important;
    font-feature-settings: "liga" off, "clig" off;
    /* Typography/Small Text/1 - Regular */
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 150% */
  }
}

.no-pointer-events {
  pointer-events: none;
}
</style>
